import { Component } from '@theme/component';
import { getWishlist, removeFromWishlist, EVENT_NAME } from '@theme/wishlist-button';

/**
 * @typedef {object} WishlistPageRefs
 * @property {HTMLElement} grid
 * @property {HTMLElement} empty
 */

/**
 * @extends {Component<WishlistPageRefs>}
 */
class WishlistPageComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.#render();
    document.addEventListener(EVENT_NAME, this.#handleUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(EVENT_NAME, this.#handleUpdate);
  }

  /** @param {PointerEvent} event */
  remove(event) {
    const target = /** @type {HTMLElement} */ (event.target);
    const handle = target.closest('[data-handle]')?.getAttribute('data-handle');
    if (handle) removeFromWishlist(handle);
  }

  #handleUpdate = () => {
    this.#render();
  };

  async #render() {
    const { grid, empty } = this.refs;
    if (!grid || !empty) return;

    const handles = getWishlist();

    if (handles.length === 0) {
      grid.replaceChildren();
      empty.hidden = false;
      return;
    }

    empty.hidden = true;

    const products = await Promise.all(
      handles.map(async (handle) => {
        try {
          const response = await fetch(`/products/${handle}.js`);
          if (!response.ok) return null;
          return await response.json();
        } catch {
          return null;
        }
      })
    );

    grid.replaceChildren(
      ...products
        .map((product, index) => (product ? this.#buildCard(product, handles[index]) : null))
        .filter((card) => card !== null)
    );
  }

  /**
   * @param {any} product
   * @param {string} handle
   * @returns {HTMLElement}
   */
  #buildCard(product, handle) {
    const card = document.createElement('div');
    card.className = 'wishlist-card';
    card.dataset.handle = handle;

    const shortTitle = product.title.split('|')[0].trim();
    const image = product.featured_image
      ? `<img src="${product.featured_image}" alt="${shortTitle}" width="300" height="300" loading="lazy">`
      : '';

    const badge = this.#buildBadge(product);
    const priceHtml = this.#buildPrice(product);
    const swatchesHtml = this.#buildSwatches(product);

    card.innerHTML = `
      <a href="${product.url}" class="wishlist-card__media">
        ${image}
        ${badge}
      </a>
      <div class="wishlist-card__info">
        <a href="${product.url}" class="wishlist-card__title">${shortTitle}</a>
        <div class="wishlist-card__price">${priceHtml}</div>
        ${swatchesHtml}
      </div>
      <button type="button" class="wishlist-card__remove" on:click="/remove" aria-label="${this.dataset.labelRemove || 'Remove'}">
        <span class="svg-wrapper">${this.dataset.removeIcon || ''}</span>
      </button>
    `;

    return card;
  }

  /** @param {any} product */
  #buildBadge(product) {
    if (product.available === false) {
      return `<div class="product-badges product-badges--top-left" style="--padding-block-start: 12px; --padding-inline-start: 12px; --border-radius: 0px;"><div class="product-badges__badge product-badges__badge--rectangle color-custom-badge-sold-out">${this.dataset.labelSoldOut || 'Sold out'}</div></div>`;
    }

    const forTag = (product.tags || []).find((tag) => tag.startsWith('for-'));
    if (forTag) {
      const audienceLabels = {
        'for-mom': this.dataset.labelForMom,
        'for-dad': this.dataset.labelForDad,
        'for-husband': this.dataset.labelForHusband,
        'for-wife': this.dataset.labelForWife,
        'for-couples': this.dataset.labelForCouples,
        'for-family': this.dataset.labelForFamily,
        'for-pets': this.dataset.labelForPets,
        'for-friends': this.dataset.labelForFriends,
      };
      const label =
        audienceLabels[forTag] ||
        `For ${forTag.replace('for-', '').replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())}`;
      return `<div class="product-badges product-badges--top-left" style="--padding-block-start: 12px; --padding-inline-start: 12px; --border-radius: 0px;"><div class="product-badges__badge product-badges__badge--gift-ready"><span class="svg-wrapper">${this.dataset.accountIcon || ''}</span>${label}</div></div>`;
    }

    return `<div class="product-badges product-badges--top-left" style="--padding-block-start: 12px; --padding-inline-start: 12px; --border-radius: 0px;"><div class="product-badges__badge product-badges__badge--gift-ready"><span class="svg-wrapper">${this.dataset.giftIcon || ''}</span>${this.dataset.labelGiftReady || 'Gift-ready'}</div></div>`;
  }

  /** @param {any} product */
  #buildPrice(product) {
    const price = this.#formatMoney(product.price);
    if (product.compare_at_price > product.price) {
      const comparePrice = this.#formatMoney(product.compare_at_price);
      return `<span>${price}</span> <s class="compare-at-price">${comparePrice}</s>`;
    }
    return `<span>${price}</span>`;
  }

  /** @param {any} product */
  #buildSwatches(product) {
    const colorOption = (product.options || []).find((option) =>
      ['color', 'finish', 'material', 'choose finish'].includes((option.name || '').toLowerCase())
    );
    if (!colorOption || !colorOption.values?.length) return '';

    const colorMap = [
      [/rose/i, '#e6b8a2'],
      [/white gold|silver|steel/i, '#c9ccd1'],
      [/gold/i, '#d4af37'],
      [/black/i, '#2b2b2b'],
    ];

    const dots = colorOption.values
      .slice(0, 3)
      .map((value) => {
        const match = colorMap.find(([pattern]) => pattern.test(value));
        const dot = match ? match[1] : '#d9d9d9';
        return `<span class="hanna-card-swatch" style="background:${dot}" title="${value}"></span>`;
      })
      .join('');

    const remaining = colorOption.values.length - 3;
    const more = remaining > 0 ? `<span class="hanna-card-swatch-more">+${remaining}</span>` : '';

    return `<div class="hanna-card-swatches">${dots}${more}</div>`;
  }

  /** @param {number} cents */
  #formatMoney(cents) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency',
        currency: this.dataset.currency || 'USD',
      }).format(cents / 100);
    } catch {
      return (cents / 100).toFixed(2);
    }
  }
}

if (!customElements.get('wishlist-page-component')) {
  customElements.define('wishlist-page-component', WishlistPageComponent);
}
